import type { ReactNode } from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';

import type { CompanyBillingSummaryDto } from '@shared/services/apiService';

import {
  billingDate as shortDate,
  billingRemainingPercent,
  formatCreditUnits,
} from '../model/formatBillingUnits';

export const BillingCreditBalance = ({
  summary,
  availableUnits,
  compact = false,
  actions,
}: {
  summary: CompanyBillingSummaryDto;
  availableUnits: string;
  compact?: boolean;
  actions?: ReactNode;
}) => {
  const previous = !summary.plan && summary.previousPeriod;
  const plan = summary.plan ?? (previous ? previous.plan : null);
  const balance = summary.balance ?? (previous ? previous.balance : null);
  if (!plan || !balance) return null;

  const scale = plan.creditScale;
  const credit = (units: string) => formatCreditUnits(units, scale);
  const trial = summary.status === 'trial';
  const current = !previous && ['trial', 'paid'].includes(summary.status);
  const remaining = billingRemainingPercent(balance.availableUnits, balance.grantedUnits);
  const endsAt = summary.endsAt ?? (previous ? previous.endsAt : null);
  // This field contains compatible spendable funds, not the entire retained wallet.
  const topUp = summary.topUpAvailableUnits ?? '0';

  const amount = (
    <Box role="group" aria-label="Available credits">
      <Stack direction="row" alignItems="baseline" spacing={1} useFlexGap flexWrap="wrap">
        <Typography
          variant="h2"
          component="p"
          fontWeight={600}
          sx={{ overflowWrap: 'anywhere', minWidth: 0, fontVariantNumeric: 'tabular-nums' }}>
          {credit(availableUnits)}
        </Typography>
        <Typography color="text.secondary">credits available</Typography>
      </Stack>
      {(!compact || !current || !summary.admissionEnabled) && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {current && summary.admissionEnabled
            ? 'Available for new calls'
            : 'New calls are paused. Credit details remain available below.'}
        </Typography>
      )}
    </Box>
  );

  return (
    <Stack component="section" aria-label="Credits" spacing={compact ? 1 : 2.5}>
      {compact ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
              Credit balance
            </Typography>
            {amount}
          </Box>
          {actions}
        </Stack>
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="h6" component="h2">
              Credits
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {trial ? 'Trial' : previous ? 'Previous period' : 'Current period'}
            </Typography>
          </Stack>
          {amount}
        </>
      )}
      {compact && (
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={1}
          useFlexGap
          flexWrap="wrap">
          {current ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {trial ? 'Trial' : 'Plan'}: {credit(balance.availableUnits)} · Expires{' '}
                {shortDate(endsAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Top-ups: {credit(topUp)} · No expiry
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Previous period ended {shortDate(endsAt)} · Credits are not available for new calls
            </Typography>
          )}
        </Stack>
      )}
      {!compact && current && remaining !== null && (
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={1}
            useFlexGap
            flexWrap="wrap">
            <Typography variant="body2">{trial ? 'Trial credits' : 'Plan credits'}</Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {credit(balance.availableUnits)} of {credit(balance.grantedUnits)} left
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={remaining}
            aria-label={trial ? 'Trial credits remaining' : 'Plan credits remaining'}
            aria-valuetext={`${credit(balance.availableUnits)} of ${credit(balance.grantedUnits)} credits remaining`}
            sx={{ my: 1.25, height: 6, borderRadius: 1 }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={1}
            useFlexGap
            flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              {credit(balance.spentUnits)} used
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expires {shortDate(endsAt)}
            </Typography>
          </Stack>
        </Box>
      )}
      {!compact && topUp !== '0' && (
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box>
            <Typography variant="body2">Top-up credits available</Typography>
            <Typography variant="caption" color="text.secondary">
              Top-up credits never expire
            </Typography>
          </Box>
          <Typography
            sx={{ fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere', minWidth: 0 }}>
            {credit(topUp)}
          </Typography>
        </Stack>
      )}
      {current && balance.heldUnits !== '0' && (
        <Typography variant="body2" color="text.secondary">
          {credit(balance.heldUnits)} credits reserved for calls · Already excluded from your
          available balance
        </Typography>
      )}
      <Box
        component="details"
        sx={{
          pt: compact ? 0 : 1.5,
          borderTop: compact ? 0 : '1px solid',
          borderColor: 'divider',
        }}>
        <Box
          component="summary"
          sx={{ color: 'primary.main', cursor: 'pointer', typography: 'body2', py: 0.5 }}>
          Balance breakdown
        </Box>
        <Box
          component="dl"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) minmax(0,auto)',
            gap: 1.25,
            my: 2,
            '& dt': { color: 'text.secondary', typography: 'body2' },
            '& dd': {
              m: 0,
              textAlign: 'right',
              overflowWrap: 'anywhere',
              fontVariantNumeric: 'tabular-nums',
              typography: 'body2',
            },
          }}>
          {[
            [
              previous ? 'Included in previous period' : 'Included in this period',
              balance.grantedUnits,
            ],
            [previous ? 'Used in previous period' : 'Used this period', balance.spentUnits],
            ['Reserved for calls', balance.heldUnits],
            ['Expired credits', balance.expiredUnits],
            ...(!previous && topUp !== '0' ? [['Top-up credits available', topUp]] : []),
          ].map(([label, units]) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Box component="dt">{label}</Box>
              <Box component="dd">{credit(units)}</Box>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" component="p">
          Reserved credits are held for active or unsettled calls. They are not an extra charge.{' '}
          {trial
            ? 'Unused trial credits expire when the trial ends or a paid plan activates.'
            : 'Unused plan credits expire when the service period ends.'}
        </Typography>
      </Box>
    </Stack>
  );
};
